import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = 'super_admin' | 'admin' | 'advisor' | 'viewer' | 'content_editor' | 'applicant';

export interface UserRole {
  role: AppRole;
  department_id: string | null;
  department_name: string | null;
  expires_at: string | null;
  scope_type: 'global' | 'department' | 'user';
}

export function useRoleCheck() {
  return useQuery({
    queryKey: ["role-check"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      console.log('🔍 useRoleCheck - User:', user?.id, user?.email);
      
      if (!user) {
        console.log('❌ useRoleCheck - No user authenticated');
        return { 
          isAuthenticated: false,
          roles: [] as UserRole[],
          isSuperAdmin: false,
          isAdmin: false,
          isAdvisor: false,
          isViewer: false,
          isContentEditor: false,
        };
      }

      // Fetch effective roles
      const { data: roles, error } = await supabase
        .from("user_effective_roles")
        .select("*")
        .eq("user_id", user.id);

      console.log('🔍 useRoleCheck - Query result:', { roles, error, userId: user.id });

      if (error) {
        console.error("❌ Error fetching user roles:", error);
        return { 
          isAuthenticated: true,
          roles: [],
          isSuperAdmin: false,
          isAdmin: false,
          isAdvisor: false,
          isViewer: false,
          isContentEditor: false,
        };
      }

      const effectiveRoles = (roles || []) as UserRole[];
      
      console.log('✅ useRoleCheck - Effective roles:', effectiveRoles);
      
      return { 
        isAuthenticated: true,
        roles: effectiveRoles,
        isSuperAdmin: effectiveRoles.some(r => r.role === 'super_admin'),
        isAdmin: effectiveRoles.some(r => r.role === 'admin' || r.role === 'super_admin'),
        isAdvisor: effectiveRoles.some(r => ['advisor', 'admin', 'super_admin'].includes(r.role)),
        isViewer: effectiveRoles.some(r => r.role === 'viewer'),
        isContentEditor: effectiveRoles.some(r => r.role === 'content_editor'),
      };
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: true,
  });
}

export function useHasPermission(resource: string, action: string) {
  return useQuery({
    queryKey: ["permission-check", resource, action],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return false;

      // Call the has_permission function
      const { data, error } = await supabase.rpc('has_permission', {
        _user_id: user.id,
        _resource: resource,
        _action: action
      });

      if (error) {
        console.error("Error checking permission:", error);
        return false;
      }

      return data as boolean;
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useDepartments() {
  const { data: roleData } = useRoleCheck();
  
  return useQuery({
    queryKey: ["user-departments"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return [];

      const { data, error } = await supabase.rpc('get_user_departments', {
        _user_id: user.id
      });

      if (error) {
        console.error("Error fetching user departments:", error);
        return [];
      }

      return data as Array<{ department_id: string; role: AppRole }>;
    },
    enabled: !!roleData?.isAuthenticated,
    staleTime: 1000 * 60 * 5,
  });
}
